import React, { useMemo } from 'react';
import { Polyline, Marker, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import { PUNCTUALITY_GOOD_PCT, PUNCTUALITY_FAIR_PCT, MIN_TRAINS_FOR_VERDICT } from './trainPosition';
import { routeLabel } from './routeLabel';

// Traffic-light colors follow the daily report's thresholds (>= 80 / >= 65), not the
// concept's 85/65 — two different traffic lights for the same figure would be worse than
// either. Grey means "not enough trains to judge" (report: min. 3).
export const ampelColor = (onTimePct) => {
  if (onTimePct === null || onTimePct === undefined) return '#6e7681';
  if (onTimePct >= PUNCTUALITY_GOOD_PCT) return '#3fb950';
  if (onTimePct >= PUNCTUALITY_FAIR_PCT) return '#f0b429';
  return '#f85149';
};

// Delay growth per section: the report calls > +1 min bad, < -1 min good (train catches up)
export const deltaColor = (deltaMin) => {
  if (deltaMin === null || deltaMin === undefined) return '#6e7681';
  if (deltaMin > 1) return '#f85149';
  if (deltaMin > -1) return '#f0b429';
  return '#3fb950';
};

export const kbsColor = (status, metric) => (
  metric === 'delta' ? deltaColor(status?.delta_avg_min) : ampelColor(status?.on_time_pct)
);

// Every figure here can be absent: below the minimum sample the aggregate stores NULL
// instead of 0, so that "no verdict" cannot be read as "perfectly punctual". `== null`
// catches undefined too - a field the API stops sending should read as no verdict, not
// crash the layer.
const fmtValue = (status, metric) => {
  if (!status) return null;
  if (metric === 'delta') {
    if (status.delta_avg_min == null) return null;
    return `${status.delta_avg_min >= 0 ? '+' : ''}${status.delta_avg_min.toFixed(1)}`;
  }
  if (status.on_time_pct == null) return null;
  return `${status.on_time_pct.toFixed(0)} %`;
};

const LABEL_MIN_ZOOM = 9;

const midpointOf = (line) => line[Math.floor(line.length / 2)];

// react-leaflet compares icons by reference and swaps the DOM node on every new one, so a
// label built afresh on each render is a label torn down and rebuilt on each render
const labelIconCache = new Map();
const labelIcon = (number, color, value, isPinned) => {
  const key = `${number}|${color}|${value}|${isPinned}`;
  let icon = labelIconCache.get(key);
  if (!icon) {
    icon = L.divIcon({
      className: 'kbs-label-icon',
      html: `<span class="kbs-label${isPinned ? ' pinned' : ''}" style="border-color:${color}">`
        + `${number}${value ? `<b style="color:${color}">${value}</b>` : ''}</span>`,
      iconSize: [0, 0],
      iconAnchor: [0, 0],
    });
    labelIconCache.set(key, icon);
  }
  return icon;
};

/**
 * Kursbuchstrecken as polylines. In "ampel" mode each KBS is colored either by its on-time
 * percentage or by its average delay growth for the selected operating day. Marked (pinned)
 * strecken keep their label and a heavier stroke at any zoom level so several can be
 * compared at once.
 *
 * The map around it repaints several times a second to move the trains. react-leaflet takes
 * a new pathOptions object as a new style, so every repaint used to restyle every route and
 * redraw the whole canvas although nothing on the rails had changed. The polylines are built
 * from their own inputs only, and the layer is memoised against the train ticker.
 */
const KbsLayer = ({
  geometry, statusByNumber, ampel, metric = 'punctuality',
  zoom, selectedKbs, pinnedKbs, onSelect,
}) => {
  const lines = useMemo(() => geometry || [], [geometry]);
  const showLabels = zoom >= LABEL_MIN_ZOOM;

  const polylines = useMemo(() => (
    lines.map(kbs => {
      const status = statusByNumber?.get(kbs.kbs_number);
      const isSelected = selectedKbs === kbs.kbs_number;
      const isPinned = pinnedKbs?.has(kbs.kbs_number);
      const color = ampel ? kbsColor(status, metric) : '#58a6ff';
      const weight = isSelected ? 7 : isPinned ? 6 : ampel ? 4 : 3;
      const opacity = isSelected || isPinned ? 1 : ampel ? 0.85 : 0.45;

      return kbs.geometry.map((line, i) => (
        <Polyline
          key={`${kbs.kbs_number}-${i}`}
          positions={line}
          pathOptions={{ color, weight, opacity, lineCap: 'round' }}
          eventHandlers={{ click: () => onSelect?.(kbs.kbs_number) }}
        >
          <Tooltip sticky>
            <strong>{routeLabel(kbs.kbs_number, kbs.country)}</strong> — {kbs.kbs_name}
            {status && (
              <>
                <br />
                {status.train_count} Züge ·{' '}
                {status.on_time_pct != null
                  ? `${status.on_time_pct.toFixed(1)} % pünktlich`
                  : `zu wenig Daten (< ${MIN_TRAINS_FOR_VERDICT} Züge)`}
                {status.delay_avg_min != null && (
                  <><br />Ø Verspätung {status.delay_avg_min.toFixed(1)} min</>
                )}
                {status.delta_avg_min != null && (
                  <>{status.delay_avg_min != null ? ' · ' : <br />}
                    Ø Zuwachs {status.delta_avg_min >= 0 ? '+' : ''}
                    {status.delta_avg_min.toFixed(1)} min</>
                )}
              </>
            )}
          </Tooltip>
        </Polyline>
      ));
    })
  ), [lines, statusByNumber, ampel, metric, selectedKbs, pinnedKbs, onSelect]);

  return (
    <>
      {polylines}

      {lines.map(kbs => {
        const status = statusByNumber?.get(kbs.kbs_number);
        const isPinned = pinnedKbs?.has(kbs.kbs_number);
        // In the traffic-light mode every KBS that has a verdict keeps its badge at any
        // zoom: that badge *is* the reading, and it is a far easier click target than a
        // 4 px line.
        const alwaysShown = ampel && status?.enough_data;
        if (!showLabels && !isPinned && !alwaysShown) return null;
        const line = kbs.geometry[0];
        if (!line || line.length === 0) return null;
        const color = ampel ? kbsColor(status, metric) : '#58a6ff';
        const value = ampel ? fmtValue(status, metric) : null;
        return (
          <Marker
            key={`label-${kbs.kbs_number}`}
            position={midpointOf(line)}
            icon={labelIcon(kbs.kbs_number, color, value, isPinned)}
            eventHandlers={{ click: () => onSelect?.(kbs.kbs_number) }}
          />
        );
      })}
    </>
  );
};

export default React.memo(KbsLayer);
